/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import keys from 'lodash-es/keys';
import omit from 'lodash-es/omit';
import { MatchPosition, Tag, Tags } from 'store/types';
import MemoChip, { ChipClickHandler } from 'components/memoChip/memoChip';
import { each, size } from 'lodash-es';
import { useSearch } from './useSearch';
import { SpecialChipType } from 'controls/search/helperSearch/helperSearch';

export type GetChipFunction = (
  options: {
    isDeletable: boolean;
    specialType?: SpecialChipType;
    isFocused?: boolean;
    ref?: React.RefObject<HTMLDivElement>;
  },
  tag: Tag
) => React.ReactElement;

// Стратегия контрола поиска, предоставляющая API сторонним компонентам, зависящим от состояния поиска
export const useSearchControl = ({
  forceTags = null,
  onSelect,
  onDelete,
  onUpdate,
  onSearchChange,
  onMatchChange,
  limit = 100000,
  isURLSync = false,
  matchPosition = 'start',
}: {
  forceTags?: Tags | null;

  onSelect?: (tag: Tag) => void;
  onDelete?: (tag: Tag) => void;
  onUpdate?: (selected: Tags) => void;
  onSearchChange?: (search: string) => void;
  onMatchChange?: (match: Tags) => void;

  limit?: number;

  // Опция для generalSearch, чтобы синхронизироваться с поисковой строкой
  isURLSync?: boolean;
  matchPosition?: MatchPosition;
} = {}) => {
  const {
    match,
    search,
    setSearch,
    selected,
    setSelected,
    available,
    setAvailable,
    availableHash,
    top,
  } = useSearch({
    isURLSync,
    matchPosition,
  });
  const isFull = size(selected) >= limit;

  const clearAll = () => {
    onDelete && each(selected, (tag) => onDelete(tag));
    setSearch('');
    setSelected({});
  };

  // Когда меняется forceTags, selected ставится в соответствии с ними, available сам обновится вслед в другом useEffect
  const initialHash = JSON.stringify(keys(forceTags));
  useEffect(() => {
    if (isFull || forceTags === null) {
      return;
    }

    setSelected(forceTags || {});
  }, [initialHash]);

  useEffect(() => {
    setAvailable(omit(match, keys(selected)));
    onMatchChange && onMatchChange(match);
  }, [match]);

  useEffect(() => {
    onSearchChange && onSearchChange(search);
  }, [search]);

  useEffect(() => {
    setAvailable(omit(match, keys(selected)));

    if (onUpdate) {
      onUpdate(selected);
    }
  }, [selected]);

  const ChipOnDelete: ChipClickHandler = (tag, e) => {
    e.preventDefault();
    e.stopPropagation();

    onDelete && onDelete(tag);

    setSelected((sel) => {
      return omit(sel, tag.name);
    });
  };

  const ChipOnClick: ChipClickHandler = (tag: Tag) => {
    // Если переполнен - сначала удалим выбранный
    if (isFull) {
      const k = keys(selected);
      const oldTag = selected[k[k.length - 1]];
      onDelete && onDelete(oldTag);
      setSelected((sel) => {
        return omit(sel, oldTag.name);
      });
    }

    onSelect && onSelect(tag);

    setSelected((sel) => {
      return { ...sel, [tag.name]: tag };
    });

    setSearch('');
  };

  const getChip: GetChipFunction = (
    { isDeletable, specialType, isFocused, ref },
    tag
  ) => {
    const chipProps = isDeletable
      ? {
          onIconClick: ChipOnDelete,
        }
      : {};

    return (
      <MemoChip
        className={specialType ? `chipSpecial_${specialType}` : ''}
        tag={tag}
        key={tag.name}
        isFocused={isFocused}
        onClick={ChipOnClick}
        ref={ref}
        {...chipProps}
      />
    );
  };

  return {
    selected,
    setSelected,
    available,
    getChip: getChip,
    clearAll: clearAll,
    search,
    setSearch,
    availableHash,
    top,
  };
};
